import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlansStripeMeta, listPlansApi, plansStripeMeta, reorderPlansApi, syncPlans,
} from '../../api/platform';
import { LIMIT_KEYS, PlanSummary, PlanSyncReport, atLeastPlatform } from '../../shared/types';
import {
  INFINITY, LIMIT_ENFORCED_AT, LIMIT_LABEL, formatLimit, limitsDigest, longDate, money, planDraft,
} from './planDisplay';
import { RowCard, RowMenu, TableCard } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';
import { usePlatform } from './PlatformLayout';

/** What the Draft pill says when you point at it. */
export const DRAFT_TITLE = 'Not pushed to Stripe yet — it becomes live on its first push';

/**
 * A plan somebody can buy that Stripe has never heard of: priced, with no
 * Product. "+ New plan" makes exactly this, and it stays a Draft until its
 * first push creates the Product (a Free plan never gets one, so it never is).
 */
export const neverPushed = (p: Pick<PlanSummary, 'price_monthly_cents' | 'stripe_product_id'>) =>
  p.price_monthly_cents !== null && !p.stripe_product_id;

/**
 * The plan catalogue (R12, screen 40).
 *
 * Form-Magic owns the plans; Stripe owns the money. These rows are the
 * source of truth for what a customer may do, which is why they live here
 * rather than in Stripe metadata — a ceiling has to be answerable without a
 * network call. The Stripe column shows what has actually been pushed, and a
 * draft price is loudly a draft until someone pushes it.
 *
 * The page head says where the catalogue stands with Stripe; the plans sit in
 * one table card (order arrows first, the name — now also the way into the
 * editor — second), then the limits matrix and the one webhook endpoint. On a
 * phone the table is a card per plan carrying the same arrows, links and menu.
 */
export default function PlansAdmin() {
  const { role } = usePlatform();
  const phone = useMediaQuery(PHONE);
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  const [meta, setMeta] = useState<PlansStripeMeta>({ stripeMode: null, webhookUrl: '', webhookHealthy: null });
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState<PlanSyncReport | null>(null);
  const [copied, setCopied] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const canEdit = atLeastPlatform(role, 'admin');
  const navigate = useNavigate();

  const load = () => {
    setLoadFailed(false);
    listPlansApi()
      .then((r) => {
        setPlans(r.plans);
        setMeta(plansStripeMeta(r.stripe));
        setError('');
      })
      .catch((e) => { setError(e.message); setLoadFailed(true); });
  };
  useEffect(load, []);

  async function onSync() {
    setSyncing(true);
    setError('');
    try {
      const r = await syncPlans();
      setReport({ plans: r.report });
    } catch (err: any) { setError(err.message); }
    setSyncing(false);
  }

  /**
   * Move one plan a step and persist the whole resulting order.
   *
   * Optimistic, so the arrows stay responsive; a refusal puts the catalogue
   * back to what the server actually holds rather than leaving the screen
   * claiming an order nobody saved.
   */
  function move(index: number, by: -1 | 1) {
    const list = [...(plans || [])];
    const to = index + by;
    if (to < 0 || to >= list.length) return;
    [list[index], list[to]] = [list[to], list[index]];
    setPlans(list);
    setError('');
    reorderPlansApi(list.map((p) => p.id))
      .then((r) => setPlans(r.plans))
      .catch((e) => {
        setError(e.message);
        listPlansApi().then((r) => setPlans(r.plans)).catch(() => undefined);
      });
  }

  // Every seeded plan shares one discount, so the header can name it. If
  // somebody gives one plan its own, the header stops claiming a single rate.
  const discounts = new Set((plans || []).filter((p) => p.price_monthly_cents !== null)
    .map((p) => p.annual_discount_pct));
  const annualHeader = discounts.size === 1
    ? `Annual · ${[...discounts][0]}% off`
    : 'Annual · up front';

  // A draft is a saved price Stripe does not carry yet — a changed one, or a
  // priced plan never pushed at all. Either way the head must not say "in sync".
  const draftCount = (plans || []).filter((p) => planDraft(p) !== null || neverPushed(p)).length;
  // The newest push stamp in the catalogue: when Stripe last heard from here.
  const lastPush = (plans || []).map((p) => p.stripe_synced_at).filter((s): s is string => !!s).sort().pop() || null;

  const reorder = (p: PlanSummary, i: number) => (
    // The order is the picker's order AND the ladder — see the note under the table.
    <div className="pl-reorder-pair">
      <button type="button" className="fs-btn neutral sm pl-reorder" title={`Move ${p.name} up`}
        aria-label={`Move ${p.name} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
      <button type="button" className="fs-btn neutral sm pl-reorder" title={`Move ${p.name} down`}
        aria-label={`Move ${p.name} down`} disabled={i === (plans || []).length - 1} onClick={() => move(i, 1)}>↓</button>
    </div>
  );

  const menu = (p: PlanSummary, draft: boolean) => (
    <RowMenu label={p.name} open={openMenuId === p.id} onToggle={(open) => setOpenMenuId(open ? p.id : null)}>
      <button onClick={() => navigate(`/platform/plans/${p.id}`)}>
        {canEdit ? (draft ? 'Edit plan · push draft…' : 'Edit plan…') : 'View plan…'}
      </button>
      <button onClick={() => navigate(`/platform?plan=${p.key}`)}>See its accounts</button>
    </RowMenu>
  );

  /** The name with its chip and pills — the chip span first, the name span second. */
  const nameLine = (p: PlanSummary, i: number) => (
    <div className="pl-plan-cell">
      <span className={`pl-chip pl-chip-${Math.min(i, 3)}`} />
      <span className="pl-plan-name"><Link to={`/platform/plans/${p.id}`}>{p.name}</Link></span>
      {p.popular && <span className="fs-pill identity">popular</span>}
      {p.retired_at && <span className="fs-pill neutral">retired</span>}
      {neverPushed(p) && <span className="fs-pill neutral" title={DRAFT_TITLE}>Draft</span>}
    </div>
  );

  const rowClass = (p: PlanSummary) =>
    [p.retired_at ? 'pl-row-retired' : '', planDraft(p) ? 'pl-row-draft' : ''].filter(Boolean).join(' ') || undefined;

  const orderNote = canEdit ? (
    <span className="pl-foot-note">
      <b>This order is two things at once.</b> It is the order the cards appear in on the
      public pricing page and in a customer's plan picker, and it is what &ldquo;a bigger
      plan&rdquo; means — moving a plan up makes it an upgrade from everything now below
      it, and a downgrade from everything above. Prices are not re-checked against it.
    </span>
  ) : undefined;

  return (
    <div className="pl-page pl-plans">
      <div className="fs-page-head pl-head">
        <div className="text">
          <h1>Plans</h1>
          <div className="fs-page-sub pl-plans-sync">
            {meta.stripeMode ? (
              <>
                <span className={`fs-pill ${draftCount ? 'warn' : 'ok'}`}>
                  Stripe {meta.stripeMode} · {draftCount ? `${draftCount} draft${draftCount === 1 ? '' : 's'} not pushed` : 'in sync'}
                </span>
                <span>
                  {lastPush ? `Last pushed ${longDate(lastPush)}.` : 'Nothing pushed yet.'} Prices reach Stripe only
                  when a plan is pushed.
                </span>
              </>
            ) : (
              /* No Stripe credentials on this install, so there is nothing to be in
                 sync with. Said plainly rather than shown as health. */
              <>
                <span className="fs-pill neutral"
                  title="No Stripe secret key — set one in Platform settings, or as STRIPE_SECRET_KEY">
                  Stripe not connected
                </span>
                <span>Prices here are the catalogue's own; nothing reaches Stripe until a key is set.</span>
              </>
            )}
          </div>
        </div>
        <div className="pl-head-actions">
          {meta.stripeMode && (
            <button className="fs-btn neutral" disabled={syncing} onClick={onSync}>
              {syncing ? 'Syncing…' : 'Sync from Stripe'}
            </button>
          )}
          {canEdit && <Link className="fs-btn primary" to="/platform/plans/new">+ New plan</Link>}
        </div>
      </div>
      {error && <div className="ad-err">{error}</div>}

      {report && <SyncReport report={report} onClose={() => setReport(null)} />}

      {!plans ? (
        loadFailed ? (
          <div className="fs-card pad st-muted">
            The plans could not be loaded.{' '}
            <button className="fs-btn neutral sm" onClick={load}>Try again</button>
          </div>
        ) : <div className="fs-card pad">Loading…</div>
      ) : (
        <>
          {phone ? (
            /* The phone: a card per plan (ABC4-03), the same arrows, links and menu. */
            <TableCard
              hint="Form-Magic is the source of truth for limits; prices are pushed to Stripe."
              footer={orderNote}
            >
              <div className="pl-plan-cards">
                {plans.length === 0 && <div className="st-card-body st-muted">No plans yet.</div>}
                {plans.map((p, i) => {
                  const draft = planDraft(p);
                  return (
                    <div key={p.id} className={`pl-plan-card${rowClass(p) ? ` ${rowClass(p)}` : ''}`}>
                      <div className="pl-plan-card-main">
                        {nameLine(p, i)}
                        <div className="fs-cell-sub">{limitsDigest(p.limits, p.features)}</div>
                        <div className="pl-plan-card-facts">
                          <span>{money(p.price_monthly_cents, p.currency)}{p.price_monthly_cents !== null ? '/mo' : ''}</span>
                          {p.price_annual_cents !== null && <span>{money(p.price_annual_cents, p.currency)}/yr</span>}
                          <Link to={`/platform?plan=${p.key}`} className="pl-count-link">
                            {p.account_count} account{p.account_count === 1 ? '' : 's'}
                          </Link>
                          <span className={`fs-pill ${p.public ? 'ok' : 'neutral'}`}>{p.public ? 'public' : 'hidden'}</span>
                        </div>
                        <StripeCell plan={p} />
                      </div>
                      <div className="pl-plan-card-side">
                        {menu(p, !!draft)}
                        {canEdit && reorder(p, i)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TableCard>
          ) : (
            <TableCard
              className="pl-plans-card"
              hint={(
                <>
                  Form-Magic is the source of truth for limits; prices are pushed to Stripe. A price change
                  never edits a live Stripe Price — it creates a new one and archives the old.
                </>
              )}
              footer={orderNote}
            >
              <table className="fs-table pl-plans-table">
                <thead>
                  <tr>
                    {canEdit && <th className="pl-col-order" />}
                    <th>Plan</th>
                    <th className="num">Monthly</th>
                    <th className="num">{annualHeader}</th>
                    <th>Stripe</th>
                    <th className="num">Accounts</th>
                    <th>Visible</th>
                    <th className="pl-col-menu" />
                  </tr>
                </thead>
                <tbody>
                  {plans.length === 0 && (
                    <tr><td colSpan={canEdit ? 8 : 7} className="st-muted">No plans yet.</td></tr>
                  )}
                  {plans.map((p, i) => {
                    const draft = planDraft(p);
                    return (
                      // The row someone half-changed is the row to look at.
                      <tr key={p.id} className={rowClass(p)}>
                        {canEdit && <td>{reorder(p, i)}</td>}
                        <td>
                          {nameLine(p, i)}
                          <div className="fs-cell-sub">{limitsDigest(p.limits, p.features)}</div>
                        </td>
                        <td className="num">{money(p.price_monthly_cents, p.currency)}</td>
                        <td className="num">{money(p.price_annual_cents, p.currency)}</td>
                        <td><StripeCell plan={p} /></td>
                        <td className="num">
                          {/* The count is a door: it opens the console filtered
                              to the accounts it counted. */}
                          <Link to={`/platform?plan=${p.key}`} className="pl-count-link">{p.account_count}</Link>
                        </td>
                        <td>
                          <span className={`fs-pill ${p.public ? 'ok' : 'neutral'}`}>
                            {p.public ? 'public' : 'hidden'}
                          </span>
                        </td>
                        <td className="fs-row-actions">{menu(p, !!draft)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableCard>
          )}

          <LimitsMatrix plans={plans} />

          {meta.webhookUrl && (
            <WebhookPanel url={meta.webhookUrl} healthy={meta.webhookHealthy}
              copied={copied} onCopy={() => {
                navigator.clipboard?.writeText(meta.webhookUrl).catch(() => undefined);
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              }} />
          )}
        </>
      )}
    </div>
  );
}

/**
 * What Stripe knows about this plan.
 *
 * Free has no Stripe Product by design — a Free account has no subscription.
 * A pushed plan shows its live price id; a saved-but-unpushed change shows as
 * a draft, because that is the whole point of the push model: a typo cannot
 * reach a live customer.
 */
function StripeCell({ plan }: { plan: PlanSummary }) {
  if (plan.price_monthly_cents === null) {
    return <span className="pl-stripe-none">no Stripe product</span>;
  }
  const draft = planDraft(plan);
  return (
    <div className="pl-stripe-cell">
      {plan.stripe_price_monthly
        ? <code className="pl-stripe-id">{plan.stripe_price_monthly}</code>
        : <span className="fs-cell-sub warn pl-flush">not pushed</span>}
      <div className={`fs-cell-sub${draft ? ' warn' : ''}`}>
        {draft
          ? `draft ${money(draft.newCents, plan.currency)} not pushed`
          : plan.archived_price_count === 0
            ? 'no archived prices'
            : `${plan.archived_price_count} archived price${plan.archived_price_count === 1 ? '' : 's'}`}
      </div>
    </div>
  );
}

/**
 * The read-only reconcile: what Stripe holds, checked row by row.
 * `GET /plans/sync` never writes — pushing is the only thing that does.
 */
function SyncReport({ report, onClose }: { report: PlanSyncReport; onClose: () => void }) {
  return (
    <section className="fs-card pl-sync" aria-labelledby="pl-sync-title">
      <div className="st-card-head">
        <div className="pl-headtext">
          <h2 id="pl-sync-title">Sync from Stripe</h2>
          <div className="hint">
            Read-only: this reports differences and writes nothing. Pushing a plan is the only call that
            changes Stripe.
          </div>
        </div>
        <button className="fs-btn neutral sm" onClick={onClose}>Dismiss</button>
      </div>
      {report.plans.length === 0 && (
        <div className="st-card-body st-muted">Nothing to compare — no plan has a price or a Stripe product yet.</div>
      )}
      {report.plans.map((p) => (
        <div key={p.plan_key} className="pl-sync-plan">
          <code>{p.plan_key}</code>
          <div className="pl-sync-checks">
            {p.checks.map((c) => (
              <div key={c.name} className="sso-check">
                <span className={`sso-dot ${c.ok === 'unknown' ? '' : c.ok ? 'pass' : 'fail'}`} />
                <div>
                  <div className="sso-check-title">{c.name}</div>
                  <div className="sso-check-detail">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

/** The single webhook endpoint — one, for every plan and every account. */
function WebhookPanel({ url, healthy, copied, onCopy }: {
  url: string; healthy: boolean | null; copied: boolean; onCopy: () => void;
}) {
  return (
    <section className="fs-card pl-webhook-card" aria-labelledby="pl-webhook-title">
      <div className="st-card-head"><h2 id="pl-webhook-title">Stripe webhook</h2></div>
      <RowCard
        label="Endpoint"
        hint="Give this to Stripe once — one endpoint for every plan and every account."
        actions={(
          <>
            <button type="button" className="fs-btn neutral sm" onClick={onCopy}>{copied ? 'Copied' : 'Copy'}</button>
            {healthy === null ? (
              <span className="fs-pill neutral">no events yet</span>
            ) : healthy ? (
              <span className="fs-pill ok">receiving events</span>
            ) : (
              <span className="fs-pill danger">not receiving events</span>
            )}
          </>
        )}
      >
        <code className="pl-webhook-url">{url}</code>
      </RowCard>
    </section>
  );
}

/**
 * The whole enforcement surface on one screen.
 *
 * Every cell is the number a hard block is measured against, and the
 * Enforced at column names where — so the number and the code that reads it
 * stay findable from one another.
 */
function LimitsMatrix({ plans }: { plans: PlanSummary[] }) {
  return (
    <TableCard
      title="Limits matrix"
      hint={(
        <>
          Every cell is the number a hard block is measured against; {INFINITY} means the check is skipped
          entirely — not a very large number.
        </>
      )}
      footer={(
        <span className="pl-foot-note">
          <b>These numbers gate downgrades too.</b> A tenant cannot move to a smaller plan while its
          storage sits above that plan's ceiling — bytes cannot be made read-only. Forms and users can be
          brought under a ceiling without deleting anything, so those are self-serve.
        </span>
      )}
    >
      <table className="fs-table pl-matrix">
        <thead>
          <tr>
            <th>Limit</th>
            {plans.map((p) => <th key={p.id} className="num">{p.name}</th>)}
            <th>Enforced at</th>
          </tr>
        </thead>
        <tbody>
          {LIMIT_KEYS.map((key) => (
            <tr key={key}>
              <td className="pl-matrix-label">{LIMIT_LABEL[key]}</td>
              {plans.map((p) => (
                <td key={p.id} className={`num${p.limits[key] === null ? ' pl-unlimited' : ''}`}>
                  {formatLimit(key, p.limits[key])}
                </td>
              ))}
              <td><code className="pl-matrix-where">{LIMIT_ENFORCED_AT[key]}</code></td>
            </tr>
          ))}
          <tr>
            <td className="pl-matrix-label">SSO access</td>
            {plans.map((p) => (
              <td key={p.id} className={`num${p.features?.sso ? ' pl-unlimited' : ''}`}>
                {p.features?.sso ? '✓' : '—'}
              </td>
            ))}
            <td><code className="pl-matrix-where">Account access page</code></td>
          </tr>
        </tbody>
      </table>
    </TableCard>
  );
}
